import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, ArrowRight, Loader2, Save, ClipboardList, Briefcase } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STEPS = ["Type", "Basics", "Details", "Location & Budget", "Review"];

export default function RequirementWizard() {
  const nav = useNavigate();
  const [step, setStep] = React.useState(0);
  const [type, setType] = React.useState("staffing");
  const [saving, setSaving] = React.useState(false);
  const [f, setF] = React.useState({
    title: "", department: "", openings: 1, skills: "", experience: "", education: "",
    location: "", work_mode: "Hybrid", salary: "", timeline: "Immediate", employment_type: "Full-time", jd: "",
    service: "", project_name: "", scope: "", technology: "", budget: "", additional: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (status) => {
    setSaving(true);
    try {
      const payload = {
        type, status,
        title: type === "staffing" ? f.title : f.project_name || f.service,
        department: f.department, openings: Number(f.openings) || 1,
        skills: f.skills ? f.skills.split(",").map((s) => s.trim()).filter(Boolean) : [],
        experience: f.experience, education: f.education, location: f.location, work_mode: f.work_mode,
        salary: f.salary, budget: f.budget, timeline: f.timeline, employment_type: f.employment_type, jd: f.jd,
        service: f.service, project_name: f.project_name, scope: f.scope,
        technology: f.technology ? f.technology.split(",").map((s) => s.trim()).filter(Boolean) : [],
        additional: f.additional,
      };
      await api.post("/requirements", payload);
      toast.success(status === "draft" ? "Draft saved" : "Requirement published");
      nav("/app/requirements");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Create Requirement" description="Post a staffing or service requirement to your partner network." breadcrumb="Requirements / Create" />
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 shrink-0 ${i <= step ? "text-vs-royal" : "text-slate-400"}`}>
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${i < step ? "bg-vs-royal text-white" : i === step ? "bg-vs-light text-vs-royal ring-2 ring-vs-royal" : "bg-slate-100"}`}>
                {i < step ? <Check className="h-4 w-4" /> : `0${i + 1}`}</div>
              <span className="text-sm font-medium hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`h-px w-8 ${i < step ? "bg-vs-royal" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <Card className="max-w-3xl">
        <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}>
          {step === 0 && (
            <div className="grid sm:grid-cols-2 gap-4">
              {[["staffing", "Staffing Requirement", ClipboardList, "Source talent and manpower"], ["service", "Service Requirement", Briefcase, "Engage vendors for projects"]].map(([id, label, Icon, desc]) => (
                <button key={id} onClick={() => setType(id)} data-testid={`req-type-${id}`}
                  className={`text-left rounded-xl border p-5 transition-all ${type === id ? "border-vs-royal ring-1 ring-vs-royal bg-vs-light/30" : "border-slate-200 hover:border-slate-300"}`}>
                  <Icon className={`h-6 w-6 mb-3 ${type === id ? "text-vs-royal" : "text-slate-400"}`} />
                  <div className="font-semibold text-slate-900">{label}</div>
                  <div className="text-sm text-slate-500 mt-1">{desc}</div>
                </button>
              ))}
            </div>
          )}
          {step === 1 && type === "staffing" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Position" required><Input value={f.title} onChange={set("title")} data-testid="req-title" /></Field>
              <Field label="Department"><Input value={f.department} onChange={set("department")} /></Field>
              <Field label="Number of Openings"><Input type="number" value={f.openings} onChange={set("openings")} /></Field>
              <Field label="Employment Type"><Sel value={f.employment_type} onChange={set("employment_type")} options={["Full-time", "Contract", "Contract-to-Hire", "Part-time"]} /></Field>
            </div>
          )}
          {step === 1 && type === "service" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Service" required><Input value={f.service} onChange={set("service")} data-testid="req-service" /></Field>
              <Field label="Project Name"><Input value={f.project_name} onChange={set("project_name")} /></Field>
              <Field label="Scope" full><Textarea value={f.scope} onChange={set("scope")} /></Field>
            </div>
          )}
          {step === 2 && type === "staffing" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Skills" full><Input value={f.skills} onChange={set("skills")} placeholder="React, Node.js, AWS (comma separated)" /></Field>
              <Field label="Experience"><Input value={f.experience} onChange={set("experience")} placeholder="5+ years" /></Field>
              <Field label="Education"><Input value={f.education} onChange={set("education")} /></Field>
              <Field label="Job Description" full><Textarea value={f.jd} onChange={set("jd")} rows={4} /></Field>
            </div>
          )}
          {step === 2 && type === "service" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Technology" full><Input value={f.technology} onChange={set("technology")} placeholder="AWS, Docker, React (comma separated)" /></Field>
              <Field label="Additional Information" full><Textarea value={f.additional} onChange={set("additional")} rows={4} /></Field>
            </div>
          )}
          {step === 3 && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Location"><Input value={f.location} onChange={set("location")} /></Field>
              {type === "staffing" ? <>
                <Field label="Work Mode"><Sel value={f.work_mode} onChange={set("work_mode")} options={["Onsite", "Hybrid", "Remote"]} /></Field>
                <Field label="Salary / Budget"><Input value={f.salary} onChange={set("salary")} placeholder="18 LPA" /></Field>
                <Field label="Joining Timeline"><Sel value={f.timeline} onChange={set("timeline")} options={["Immediate", "15 days", "30 days", "60 days"]} /></Field>
              </> : <>
                <Field label="Budget"><Input value={f.budget} onChange={set("budget")} placeholder="₹50 L" /></Field>
                <Field label="Timeline"><Input value={f.timeline} onChange={set("timeline")} placeholder="6 months" /></Field>
              </>}
            </div>
          )}
          {step === 4 && (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">Review your requirement before publishing.</div>
              <div className="rounded-xl bg-slate-50 p-4 grid sm:grid-cols-2 gap-3 text-sm">
                <Review k="Type" v={type} /><Review k="Title" v={type === "staffing" ? f.title : f.project_name || f.service} />
                <Review k="Location" v={f.location} /><Review k="Openings" v={type === "staffing" ? f.openings : "—"} />
                <Review k="Skills/Tech" v={type === "staffing" ? f.skills : f.technology} /><Review k="Budget" v={type === "staffing" ? f.salary : f.budget} />
              </div>
            </div>
          )}
        </motion.div>

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-back"><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => submit("draft")} disabled={saving} data-testid="wizard-draft"><Save className="h-4 w-4 mr-1.5" />Save Draft</Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} className="bg-vs-royal text-white" data-testid="wizard-next">Next<ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            ) : (
              <Button onClick={() => submit("active")} disabled={saving} className="bg-vs-royal text-white" data-testid="wizard-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish Requirement"}</Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

const Field = ({ label, children, required, full }) => (
  <div className={full ? "sm:col-span-2" : ""}><Label>{label}{required && <span className="text-rose-500">*</span>}</Label><div className="mt-1.5">{children}</div></div>
);
const Sel = ({ value, onChange, options }) => (
  <Select value={value} onValueChange={(v) => onChange(v)}>
    <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white">{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);
const Review = ({ k, v }) => (<div><div className="text-xs uppercase tracking-wide text-slate-400">{k}</div><div className="text-slate-800 capitalize">{v || "—"}</div></div>);
